import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { Box, Grid, Skeleton, SvgIcon, Typography, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { IconFileExport, IconSearch } from '@tabler/icons-react';
import { Button, DateTimePicker, IconButton } from '@atoms';
import {
  useCustomerId,
  useIsAreasAdmin,
  useProductsByCustomer,
  useSelectedCustomerProducts,
  useShowAreaFeature,
  useUser,
} from '@hooks';
import { SelectFetch } from '@molecules';
import { SelectAreas } from '@organisms';
import { getDatesFromTheLastMonth, trackings } from '@utils';
import { DATE_PICKER_PROPS, validationSchema } from '../constants';
import useDownloadConsumptionByCategory from '../data/useDownloadConsumptionByCategory';
import {
  CONSUMPTION_BY_CATEGORY_CLICK_EVENT,
  EVENT_CONSUMPTION_BY_CATEGORY_CLEAR_FILTER,
  EVENT_CONSUMPTION_BY_CATEGORY_EXPORT,
} from '../events';
import styles from '../styles';

interface SearchBoxConsumptionByCategoryProps {
  filtersOpen?: boolean;
  setFiltersOpen?: (open: boolean) => void;
  isLoading?: boolean;
  params: Record<string, string>;
  onSubmit: (values: Record<string, string | Date>) => void;
  dataLength?: number;
}

/**
 * Displays filters
 * @param {SearchBoxConsumptionByCategoryProps} props
 * @param {object} props.params
 */
const SearchBoxConsumptionByCategory: React.FC<SearchBoxConsumptionByCategoryProps> = ({
  isLoading = false,
  params,
  onSubmit,
  dataLength,
}: SearchBoxConsumptionByCategoryProps) => {
  const { t } = useTranslation();
  const responsive = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { customerId } = useCustomerId();
  const { data: products, isLoading: isLoadingProducts } = useProductsByCustomer({ customerId });
  const previousCompanyRef = useRef<number | null>(null);
  const { fromDate, toDate } = getDatesFromTheLastMonth();
  const { download, isLoading: isLoadingConsumption } = useDownloadConsumptionByCategory();
  const initialValues = {
    fromDate,
    toDate,
    product: params?.product || '',
    areaId: '',
  };
  const { showAreaFeature } = useShowAreaFeature();
  const { user } = useUser();

  const { handleChange, setFieldValue, handleSubmit, isValid, errors, values, resetForm } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit,
    });
  const { isAreasAdmin } = useIsAreasAdmin();
  const validateArea = showAreaFeature && !isAreasAdmin ? !values?.areaId : false;

  const disableButton = isLoading || !isValid || validateArea;

  useEffect(() => {
    if (!isLoadingProducts) {
      const product = products.length === 1 ? products[0] : '';
      const values = { fromDate, toDate, product, areaId: '' };
      if (previousCompanyRef.current !== customerId || (products.length === 1 && !params.product)) {
        resetForm({ values });
        onSubmit({ product });
      }
      previousCompanyRef.current = customerId;
    }
  }, [
    fromDate,
    isLoadingProducts,
    onSubmit,
    params.product,
    products,
    resetForm,
    customerId,
    toDate,
  ]);

  return (
    <Box sx={styles.searchContainer}>
      <Box sx={styles.header}>
        <Typography variant='h4'>{t('MOVEMENTS:FILTERS_TITLE')}</Typography>
      </Box>
      <Box sx={styles.content(responsive)}>
        <Box sx={styles.firstLine(responsive)}>
          <Grid container spacing={2}>
            <Grid item xs={5} md={3}>
              <DateTimePicker
                name='fromDate'
                value={values.fromDate}
                handleChange={(date: Date) => setFieldValue('fromDate', date)}
                error={Boolean(errors.fromDate)}
                inputLabel='COMMON:FROM_DATE'
                helperText={errors.fromDate ? String(errors.fromDate) : undefined}
                {...DATE_PICKER_PROPS}
              />
            </Grid>
            <Grid item xs={5} md={3}>
              <DateTimePicker
                name='toDate'
                value={values.toDate}
                handleChange={(date: Date) => setFieldValue('toDate', date)}
                error={Boolean(errors.toDate)}
                inputLabel='COMMON:TO_DATE'
                helperText={errors.toDate ? String(errors.toDate) : undefined}
                {...DATE_PICKER_PROPS}
              />
            </Grid>
            <Grid item xs={5} md={3}>
              {isLoadingProducts ? (
                <Skeleton height={50} sx={styles.skeleton} />
              ) : (
                <SelectFetch
                  name='product'
                  label='COMMON:PRODUCT'
                  onChange={handleChange(name)}
                  value={values.product}
                  error={Boolean(errors.product)}
                  getOptions={useSelectedCustomerProducts}
                  parseOption={(value: string) => ({ text: value, value })}
                  customStyles={styles.selectFetch}
                  helperText={errors.product ? String(errors.product) : undefined}
                />
              )}
            </Grid>
            {showAreaFeature && (
              <Grid item xs={5} md={3}>
                <SelectAreas
                  value={values['areaId']}
                  onChange={(_event, value) => {
                    handleChange('areaId')(value.id);
                  }}
                  disabled={user?.isInternal && !customerId}
                  onClear={() => {
                    setFieldValue('areaId', null);
                  }}
                />
              </Grid>
            )}
          </Grid>
        </Box>
        <Box sx={styles.actionsContainer(responsive)}>
          <Button
            customStyle={styles.clearButton}
            text={t('COMMON:CLEAR_FILTER')}
            color='primary'
            variant='link'
            onClick={() => {
              trackings(
                CONSUMPTION_BY_CATEGORY_CLICK_EVENT(EVENT_CONSUMPTION_BY_CATEGORY_CLEAR_FILTER)
              );
              resetForm();
            }}
          />
          <IconButton
            aria-label={t('CONSUMPTION:DOWNLOAD')}
            tooltip='CONSUMPTION:DOWNLOAD'
            onClick={() => {
              trackings(CONSUMPTION_BY_CATEGORY_CLICK_EVENT(EVENT_CONSUMPTION_BY_CATEGORY_EXPORT));
              download(params);
            }}
            IconComponent={IconFileExport}
            isLoading={isLoadingConsumption}
            disabled={!dataLength}
          />
          <Button
            customStyle={styles.searchButton}
            startIcon={
              <SvgIcon>
                <IconSearch />
              </SvgIcon>
            }
            variant='contained'
            color='primary'
            type='submit'
            disabled={disableButton}
            onClick={handleSubmit}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SearchBoxConsumptionByCategory;
